'use client';

import React, { FC, useEffect, useRef } from 'react';
import Image, { type ImageProps } from 'next/image';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  styled
} from '@mui/material';
import {
  Card,
  CardProps,
  FormattedDate,
  TranslatedText,
  useRouterPush,
  useTranslator,
  Link
} from '@ianesthesie-kit/core';
import SurveyIcon from '../../../public/icons/survey.svg';
import MovieBeginningIcon from '../../../public/icons/movie_beginning.svg';
import ConsentFormIcon from '../../../public/icons/consent.svg';
import SmartwatchIcon from '../../../public/icons/smartwatch.svg';
import SettingsIcon from '../../../public/icons/settings.svg';
import DoctorTeamIcon from '../../../public/icons/doctor_team.svg';
import { ID } from '@ianesthesie-kit/services';
import {
  AssignedQuestionnaire,
  isPendingStatus,
  QuestionnaireStatus,
  useAssignedQuestionnaires
} from '@/modules/questionnaire';
import { AppConfigService } from '@/config';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { patientBffApi } from '@/modules/baseApi';
import {
  SurgicalEventStatus,
  UpcomingSurgery,
  useActiveFollowUpPlans,
  useActiveSurgeryInfo,
  useActiveSurgicalEvent,
  useIsFollowUpPlanEnabledOnHospital
} from '@/modules/surgicalEvent';
import { Loader } from '@/components/loader';
import { useIsVerified } from '@/modules/userConfig';
import { selectCommChannelVerificationPostponed } from '@/modules/ui';
import { motion } from 'framer-motion';
import { AlertIcon } from '@/components/icon';
import { useTerraConnect } from '@/modules/terra';
import { parseDate } from '@/util';
import { isFuture, isValid } from 'date-fns';
import { useInformedConsentQuery } from '@/modules/informedConsent';

function Icon(props: ImageProps) {
  return <Image {...props} alt={props.alt} width={48} height={48} />;
}

const defaultActionProps = { sx: { justifyContent: 'center', mt: 2 } };

type CardPropsId = CardProps & {
  id: ID;
};

export const actionTranslation: { [k in QuestionnaireStatus]?: string } = {
  [QuestionnaireStatus.IN_PROGRESS]:
    'patient-app.homePage.questionnaire.action.continue',
  [QuestionnaireStatus.COMPLETED]:
    'patient-app.homePage.questionnaire.action.overview'
};

const QuestionnaireCardAction = ({
  questionnaireStatus,
  questionnaireCode = '',
  loading
}: {
  loading?: boolean;
  questionnaireStatus: QuestionnaireStatus | undefined;
  questionnaireCode?: string;
}) => {
  const dispatch = useAppDispatch();
  const disabled = loading || !questionnaireStatus;
  const btnId =
    (questionnaireStatus && actionTranslation[questionnaireStatus]) ||
    'patient-app.homePage.questionnaire.action';
  const href =
    questionnaireStatus === QuestionnaireStatus.COMPLETED
      ? '/questionnaire/overview'
      : `/questionnaire${questionnaireCode && `/${questionnaireCode}`}`;

  const invalidate = () => {
    dispatch(patientBffApi.util.invalidateTags(['ActiveSurgicalEvent']));
  };

  return (
    <Button disabled={disabled} fullWidth sx={{ px: 0 }} onClick={invalidate}>
      <Link href={href} underline="none" color="inherit" width="100%">
        {(loading || !questionnaireStatus) && (
          <Stack justifyContent="center" alignItems="center">
            <CircularProgress size={24} thickness={4} />
          </Stack>
        )}
        {!loading && questionnaireStatus && <TranslatedText id={btnId} />}
      </Link>
    </Button>
  );
};

const Title = styled(Box)`
  color: ${(p) => alpha(p.theme.palette.text.primary, 0.8)};
  font-size: 1.3rem;
  font-weight: 500;
  margin-top: 12px;
  text-align: center;
`;

const Subtitle = styled(Box)`
  color: ${(p) => p.theme.palette.text.secondary};
  font-size: 1rem;
  font-weight: 500;
  margin-top: 2px;
  margin-bottom: -8px;
`;

const QuestionnaireCodeChip = styled(Chip)`
  margin: 8px auto 8px;
  padding: 0;
`;

const TitleAndSubtitle = ({
  title,
  subtitle,
  chipText
}: {
  title: string;
  subtitle?: string;
  chipText?: string;
}) => (
  <>
    <Title>
      <TranslatedText id={title} />
      {chipText && (
        <div>
          <QuestionnaireCodeChip label={<TranslatedText id={chipText} />} />
        </div>
      )}
    </Title>
    {subtitle && (
      <Subtitle>
        <TranslatedText id={subtitle} />
      </Subtitle>
    )}
  </>
);

const Content = styled(Box)`
  color: ${(p) => p.theme.palette.text.secondary};
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
`;

const getQuestionnaireChipTranslationKey = (aq: AssignedQuestionnaire) => {
  if (!aq?.questionnaireCode || !aq.type) {
    return undefined;
  }

  if (aq.type === 'PROMS_POSTOP') {
    return `patient-app.homePage.questionnaire.chip.${aq.questionnaireCode}`;
  }

  return `patient-app.homePage.questionnaire.chip.${aq.type}`;
};

const createQuestionnaireCardProps = (
  questionnaire: AssignedQuestionnaire
): CardPropsId => ({
  id: `Questionnaire_${questionnaire?.id}`,
  icon: <Icon src={SurveyIcon} alt="Questionnaire" />,
  content: {
    title: (
      <>
        <TitleAndSubtitle
          title="patient-app.homePage.questionnaire.title"
          subtitle="patient-app.homePage.questionnaire.subheader"
          chipText={getQuestionnaireChipTranslationKey(questionnaire)}
        />
        {/*<span style={{position: 'absolute', top: 0}}>{questionnaire.questionnaireCode} {questionnaire.type}</span>*/}
      </>
    ),
    body: (
      <Content>
        <TranslatedText id="patient-app.homePage.questionnaire.body" />
      </Content>
    )
  },
  actionsProps: defaultActionProps,
  actions: (
    <QuestionnaireCardAction
      questionnaireStatus={questionnaire.status}
      questionnaireCode={questionnaire.questionnaireCode}
    />
  )
});

const InformedConsentCardProps: CardPropsId = {
  id: 'Consent',
  icon: <Icon src={ConsentFormIcon} alt="Consent form" />,
  content: {
    title: <TitleAndSubtitle title="patient-app.homePage.consent.title" />,
    body: (
      <Content>
        <TranslatedText id="patient-app.homePage.consent.body" />
      </Content>
    )
  },
  actionsProps: defaultActionProps,
  actions: (
    <Button fullWidth sx={{ px: 0 }}>
      <Link href="/consent" underline="none" color="inherit" width="100%">
        <TranslatedText id="patient-app.homePage.consent.btn" />
      </Link>
    </Button>
  )
};

/** The sides the patient app can name, in the questionnaire's own keys. */
const PATIENT_SIDE_KEYS: Record<string, string | undefined> = {
  LEFT: 'questionnaire.CH_SurgerySide.Left',
  RIGHT: 'questionnaire.CH_SurgerySide.Right',
  BOTH: 'questionnaire.CH_SurgerySide.Both'
};

const UpcomingSurgeryList = ({
  upcomingSurgeries
}: {
  upcomingSurgeries: UpcomingSurgery[];
}) => {
  const translate = useTranslator();

  return (
    <Stack gap={1} mb={1}>
      {upcomingSurgeries.map((upcoming) => {
        // The questionnaire's patient-facing names, not the doctors' catalogue names
        const names = [
          ...(upcoming.surgeries ?? []).map((s) =>
            translate(`questionnaire.CH_SurgeryName.${s.code}`)
          ),
          ...(upcoming.userDefinedSurgeries ?? [])
        ];
        const sideKey = PATIENT_SIDE_KEYS[upcoming.surgerySide ?? ''];
        const side = sideKey ? translate(sideKey) : undefined;

        return (
          <Box key={upcoming.planId}>
            <FormattedDate
              variant="inherit"
              color="text.primary"
              fontWeight={500}
              format="PPP"
            >
              {upcoming.surgeryDate}
            </FormattedDate>
            <div>
              {names.length > 0
                ? names.join(', ')
                : translate('patient-app.homePage.upcomingSurgery.unnamed')}
              {side && ` (${side})`}
            </div>
          </Box>
        );
      })}
    </Stack>
  );
};

const createUpcomingSurgeryCardProps = (
  upcomingSurgeries: UpcomingSurgery[]
): CardPropsId => ({
  id: 'UpcomingSurgery',
  icon: <Icon src={DoctorTeamIcon} alt="Upcoming surgery" />,
  content: {
    title: (
      <TitleAndSubtitle title="patient-app.homePage.upcomingSurgery.title" />
    ),
    body: (
      <Content>
        <UpcomingSurgeryList upcomingSurgeries={upcomingSurgeries} />
        <TranslatedText id="patient-app.homePage.upcomingSurgery.body" />
      </Content>
    )
  }
});

const AnesthesiaInfoCardProps: CardPropsId = {
  id: 'Anesthesia',
  icon: <Icon src={MovieBeginningIcon} alt="Anesthesia information" />,
  content: {
    title: (
      <TitleAndSubtitle
        title="patient-app.homePage.anesthesia.title"
        subtitle="patient-app.homePage.anesthesia.subheader"
      />
    ),
    body: (
      <Content>
        <TranslatedText id="patient-app.homePage.anesthesia.body" />
      </Content>
    )
  },
  actionsProps: defaultActionProps,
  actions: (
    <Button
      component="a"
      target="_blank"
      fullWidth
      href={AppConfigService.getInstance().getLink('anesthesiaInfo')}
    >
      <TranslatedText id="patient-app.homePage.anesthesia.action" />
    </Button>
  )
};

const TerraButton = () => {
  const { openWidget } = useTerraConnect();

  return (
    <>
      <Button fullWidth onClick={openWidget} sx={{ position: 'relative' }}>
        <TranslatedText id="patient-app.homePage.terra.action" />
      </Button>
    </>
  );
};

const TerraIntegrationCardProps: CardPropsId = {
  id: 'Terra',
  icon: <Icon src={SmartwatchIcon} alt="Terra logo" />,
  content: {
    title: (
      <Title>
        <TranslatedText id="patient-app.homePage.terra.title" />
      </Title>
    ),
    body: (
      <Content>
        <TranslatedText id="patient-app.homePage.terra.body" />
      </Content>
    )
  },
  actionsProps: defaultActionProps,
  actions: <TerraButton />
};

const SettingsAction = () => (
  <Button
    component={Link}
    href="/profile"
    color="inherit"
    variant="outlined"
    fullWidth
  >
    <div style={{ paddingLeft: 6, paddingRight: 6 }}>
      <TranslatedText id="patient-app.homePage.settings.action" />
    </div>
  </Button>
);

const UnverifiedChip = () => {
  const isUserVerified = useIsVerified();
  const postponed = useAppSelector(selectCommChannelVerificationPostponed);

  if (isUserVerified || !postponed) {
    return null;
  }

  return (
    <Box
      mt={2}
      component={motion.div}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <Chip
        variant="outlined"
        color="warning"
        avatar={<AlertIcon />}
        label={
          <TranslatedText id="patient-app.homePage.settings.unverifiedChip" />
        }
      />
    </Box>
  );
};

const SettingsCardProps: CardPropsId = {
  id: 'Settings',
  icon: <Icon src={SettingsIcon} alt="My Settings" />,
  content: {
    title: (
      <Title>
        <TranslatedText id="patient-app.homePage.settings.title" />
      </Title>
    ),
    body: (
      <Content>
        <TranslatedText id="patient-app.homePage.settings.subheader" />
        <TranslatedText id="patient-app.homePage.settings.body" />
        <UnverifiedChip />
      </Content>
    )
  },
  actionsProps: defaultActionProps,
  actions: <SettingsAction />
};

const cards: CardPropsId[] = [
  AnesthesiaInfoCardProps,
  // AnemiaCardProps,
  // SmokingCardProps,
  // SleepApneaCardProps,
  SettingsCardProps
];

export const HomePage: FC = () => {
  const isUserVerified = useIsVerified();
  const { data: activeSe, isLoading: activeSeLoading } = useActiveSurgicalEvent(
    undefined,
    { skip: !isUserVerified }
  );
  const { data: surgeryInfo } = useActiveSurgeryInfo(undefined, {
    skip: !activeSe
  });
  const followUpPlanEnabled = useIsFollowUpPlanEnabledOnHospital();
  const { data: upcomingSurgeries } = useActiveFollowUpPlans(undefined, {
    skip: !activeSe || !followUpPlanEnabled
  });
  const push = useRouterPush();
  const dispatch = useAppDispatch();
  const { data: assignedQuestionnaires } = useAssignedQuestionnaires(
    undefined,
    { skip: !isUserVerified }
  );

  const { isEligible: isTerraEligible } = useTerraConnect();
  const { currentData: consent } = useInformedConsentQuery(undefined, {
    skip: !isUserVerified
  });

  const surgeryInfoSyncRetriesLeft = useRef(5);

  useEffect(() => {
    if (activeSe?.status === SurgicalEventStatus.FINALIZED) {
      const surgeryDate = parseDate(surgeryInfo?.surgeryDate);

      if (!surgeryDate || !isValid(surgeryDate)) {
        const interval = setInterval(() => {
          dispatch(patientBffApi.util.invalidateTags(['SurgeryInfo']));
          if (--surgeryInfoSyncRetriesLeft.current === 0) {
            clearInterval(interval);
          }
        }, 800);

        return () => {
          clearInterval(interval);
        };
      } else if (isFuture(surgeryDate)) {
        const anyQuestionnaireInProgress = assignedQuestionnaires?.some(
          (aq) =>
            aq.status === QuestionnaireStatus.IN_PROGRESS ||
            aq.status === QuestionnaireStatus.NOT_STARTED
        );
        if (!anyQuestionnaireInProgress) {
          push('/questionnaire/overview');
        }
      }
    }
  }, [
    activeSe?.status,
    surgeryInfo?.surgeryDate,
    push,
    assignedQuestionnaires,
    dispatch
  ]);

  const statusSyncRetriesLeft = useRef(5);

  useEffect(() => {
    if (statusSyncRetriesLeft.current === 0) {
      return;
    }
    const allCompleted = assignedQuestionnaires?.every(
      (aq) => aq.status === QuestionnaireStatus.COMPLETED
    );
    if (
      allCompleted &&
      activeSe?.status === SurgicalEventStatus.PENDING_QUESTIONNAIRE_COMPLETION
    ) {
      const invalidate = () => {
        dispatch(patientBffApi.util.invalidateTags(['ActiveSurgicalEvent']));
      };

      const interval = setInterval(() => {
        invalidate();
        if (--statusSyncRetriesLeft.current === 0) {
          clearInterval(interval);
        }
      }, 800);

      return () => {
        clearInterval(interval);
      };
    }
  }, [activeSe?.status, assignedQuestionnaires, dispatch]);

  if (activeSeLoading) {
    return <Loader />;
  }

  return (
    <Grid container rowSpacing={2} columnSpacing={4}>
      {assignedQuestionnaires
        ?.filter((aq) => isPendingStatus(aq.status))
        ?.map(createQuestionnaireCardProps)
        .map((c: CardPropsId) => (
          <Grid item key={c.id} xs={12} sm={6} md={6} lg={4}>
            <Card
              {...c}
              sx={{
                textAlign: 'center',
                height: '100%',
                position: 'relative'
              }}
            />
          </Grid>
        ))}
      {consent?.consentSent && (
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <Card
            {...InformedConsentCardProps}
            sx={{
              textAlign: 'center',
              height: '100%'
            }}
          />
        </Grid>
      )}
      {!!upcomingSurgeries?.length && (
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <Card
            {...createUpcomingSurgeryCardProps(upcomingSurgeries)}
            sx={{
              textAlign: 'center',
              height: '100%'
            }}
          />
        </Grid>
      )}
      {isTerraEligible && (
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <Card
            {...TerraIntegrationCardProps}
            sx={{
              textAlign: 'center',
              height: '100%'
            }}
          />
        </Grid>
      )}
      {cards.map((c: CardPropsId) => (
        <Grid item key={c.id} xs={12} sm={6} md={6} lg={4}>
          <Card
            {...c}
            sx={{
              textAlign: 'center',
              height: '100%'
            }}
          />
        </Grid>
      ))}
    </Grid>
  );
};
